import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../auth/AuthContext";
import { useLang } from "../../../i18n/LanguageContext";
import { storeClient } from "../api/storeClient";

const storeCopy = {
  pl: {
    groupLabels: {
      credits: "Pakiety odczytów",
      access: "Dostęp czasowy",
      full: "Morfeusz Pełny",
    },
    groupSubtitles: {
      credits: "Dokup szybkie albo głębokie odczyty wtedy, kiedy ich potrzebujesz.",
      access: "Odblokuj wybrane części Morfeusza na określony czas.",
      full: "Najprostszy dostęp do całej przestrzeni Morfeusza.",
    },
    grants: {
      quick: "Szybka",
      deep: "Głęboka",
      followup: "Pytanie po analizie",
      symbolsTrace: "Symbolika + Ślad",
      symbols: "Symbolika",
      trace: "Ślad snów",
      ai: "Analizy AI",
      deepAccess: "Głębokie odczyty",
      full: "Pełny dostęp",
      days: "dni",
    },
    accessLabels: {
      all_until: "Morfeusz Pełny",
      ai_until: "Analizy AI",
      deep_until: "Głębokie odczyty",
      symbols_until: "Symbolika",
      trace_until: "Ślad snów",
      followup_until: "Pytanie po analizie",
    },
    loginEyebrow: "Konto",
    loginTitle: "Pakiety przypisują się do Twojego konta",
    loginText: "Zaloguj się przed zakupem, aby Morfeusz mógł przypisać odczyty i dostępy do Twojej Księgi.",
    login: "Zaloguj się",
    register: "Załóż konto",
    entitlementsEyebrow: "Twoje pakiety",
    entitlementsLoading: "Sprawdzam aktualny stan pakietów…",
    quickCredits: "⚡ Szybkie",
    deepCredits: "🔋 Głębokie",
    followupCredits: "✦ Pytania",
    activeUntil: "aktywne do",
    noActiveAccess: "Nie masz jeszcze aktywnego dostępu czasowego.",
    promoUntil: "do",
    buy: "Kup",
    buying: "Otwieram płatność…",
    loginToBuy: "Zaloguj się, aby kupić",
    heroEyebrow: "Sklep",
    heroTitlePrefix: "Wybierz",
    heroTitleEm: "klucz",
    heroTitleSuffix: "do kolejnych odczytów",
    heroSubtitle: "Sklep Morfeusza jest prosty: pakiety odczytów, dostęp czasowy i pełny dostęp. Pakiety przypisują się do Twojego konta.",
    loadingOffers: "Ładuję oferty sklepu…",
    loadError: "Nie udało się pobrać ofert sklepu.",
    checkoutError: "Nie udało się rozpocząć płatności.",
    emptyGroup: "Warianty tej oferty pojawią się wkrótce.",
  },
  en: {
    groupLabels: {
      credits: "Reading packages",
      access: "Timed access",
      full: "Full Morfeusz",
    },
    groupSubtitles: {
      credits: "Add quick or deep dream readings whenever you need them.",
      access: "Unlock selected parts of Morfeusz for a chosen time.",
      full: "The simplest access to the whole Morfeusz space.",
    },
    grants: {
      quick: "Quick",
      deep: "Deep",
      followup: "Follow-up question",
      symbolsTrace: "Symbolics + Dream Trace",
      symbols: "Symbolics",
      trace: "Dream Trace",
      ai: "AI analyses",
      deepAccess: "Deep readings",
      full: "Full access",
      days: "days",
    },
    accessLabels: {
      all_until: "Full Morfeusz",
      ai_until: "AI analyses",
      deep_until: "Deep readings",
      symbols_until: "Symbolics",
      trace_until: "Dream Trace",
      followup_until: "Follow-up question",
    },
    loginEyebrow: "Account",
    loginTitle: "Packages are assigned to your account",
    loginText: "Log in before purchase so Morfeusz can assign readings and access to your Book.",
    login: "Log in",
    register: "Create account",
    entitlementsEyebrow: "Your packages",
    entitlementsLoading: "Checking your current packages…",
    quickCredits: "⚡ Quick",
    deepCredits: "🔋 Deep",
    followupCredits: "✦ Questions",
    activeUntil: "active until",
    noActiveAccess: "You do not have active timed access yet.",
    promoUntil: "until",
    buy: "Buy",
    buying: "Opening payment…",
    loginToBuy: "Log in to buy",
    heroEyebrow: "Store",
    heroTitlePrefix: "Choose a",
    heroTitleEm: "key",
    heroTitleSuffix: "to more readings",
    heroSubtitle: "The Morfeusz store is simple: reading packages, timed access and full access. Packages are assigned to your account.",
    loadingOffers: "Loading store offers…",
    loadError: "Could not load store offers.",
    checkoutError: "Could not start payment.",
    emptyGroup: "Variants of this offer will appear soon.",
  },
};

const getCopy = (lang = "pl") => storeCopy[lang === "en" ? "en" : "pl"] || storeCopy.pl;

const localizeOffer = (offer = {}, lang = "pl") => {
  if (lang !== "en") return offer;
  const promotion = offer.promotion
    ? { ...offer.promotion, label: String(offer.promotion.label_en || offer.promotion.label || "") }
    : offer.promotion;
  return {
    ...offer,
    promotion,
    title: String(offer.title_en || offer.title || ""),
    variant: String(offer.variant_en || offer.variant || ""),
    description: String(offer.description_en || offer.description || ""),
    details: String(offer.details_en || offer.details || ""),
    image_alt: String(offer.image_alt_en || offer.image_alt || ""),
    badge: String(offer.badge_en || offer.badge || ""),
    highlight: String(offer.highlight_en || offer.highlight || ""),
  };
};

const formatPrice = (price, currency = "pln", lang = "pl") => {
  const amount = Number(price || 0) / 100;
  try {
    return new Intl.NumberFormat(lang === "en" ? "en-US" : "pl-PL", {
      style: "currency",
      currency: String(currency || "pln").toUpperCase(),
    }).format(amount);
  } catch (_) {
    return `${amount.toFixed(2)} ${String(currency || "pln").toUpperCase()}`;
  }
};

const formatGrant = (grant = {}, copy = storeCopy.pl) => {
  const parts = [];
  if (grant.credits_quick) parts.push(`${grant.credits_quick} × ${copy.grants.quick}`);
  if (grant.credits_deep) parts.push(`${grant.credits_deep} × ${copy.grants.deep}`);
  if (grant.credits_followup) parts.push(`${grant.credits_followup} × ${copy.grants.followup}`);
  if (grant.access_symbols_days && grant.access_trace_days) parts.push(`${copy.grants.symbolsTrace} · ${grant.access_symbols_days} ${copy.grants.days}`);
  else {
    if (grant.access_symbols_days) parts.push(`${copy.grants.symbols} · ${grant.access_symbols_days} ${copy.grants.days}`);
    if (grant.access_trace_days) parts.push(`${copy.grants.trace} · ${grant.access_trace_days} ${copy.grants.days}`);
  }
  if (grant.access_ai_days) parts.push(`${copy.grants.ai} · ${grant.access_ai_days} ${copy.grants.days}`);
  if (grant.access_deep_days) parts.push(`${copy.grants.deepAccess} · ${grant.access_deep_days} ${copy.grants.days}`);
  if (grant.access_all_days) parts.push(`${copy.grants.full} · ${grant.access_all_days} ${copy.grants.days}`);
  return parts;
};

const getActiveAccess = (access = {}) => {
  const now = Date.now();
  return Object.entries(access || {})
    .filter(([, value]) => value && Date.parse(value) > now)
    .map(([key, value]) => ({ key, value }));
};

const accessLabel = (key, copy = storeCopy.pl) => copy.accessLabels[key] || key;

const formatDate = (value, lang = "pl") => {
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat(lang === "en" ? "en-US" : "pl-PL", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date(value));
  } catch (_) {
    return String(value);
  }
};

const formatPromotionNote = (promotion, lang = "pl", copy = storeCopy.pl) => {
  if (!promotion) return "";
  const date = promotion.ends_at ? formatDate(promotion.ends_at, lang) : "";
  if (date && date !== "—") return `${copy.promoUntil} ${date}`;
  if (promotion.type === "percent" && promotion.value) return `-${promotion.value}%`;
  return "";
};

const groupOffers = (offers = []) => offers.reduce((acc, offer) => {
  const group = offer.group || "other";
  if (!acc[group]) acc[group] = [];
  acc[group].push(offer);
  return acc;
}, {});

const pickGroupHighlight = (items = [], lang = "pl") => {
  const localized = items.map((item) => localizeOffer(item, lang));
  const withHighlight = localized.find((item) => String(item.highlight || "").trim());
  return String(withHighlight?.highlight || "").trim();
};

const StoreGate = ({ copy }) => (
  <div className="mdStore__notice mdCard" data-ui="store-login-notice">
    <p className="mdPage__eyebrow">{copy.loginEyebrow}</p>
    <h2>{copy.loginTitle}</h2>
    <p>{copy.loginText}</p>
    <div className="mdRow" data-ui="store-login-actions">
      <a className="mdBtn mdBtn--primary mdBtn--row" href="#/zaloguj?redirect_to=/sklep">{copy.login}</a>
      <a className="mdBtn mdBtn--row" href="#/rejestracja?redirect_to=/sklep">{copy.register}</a>
    </div>
  </div>
);

const EntitlementsBox = ({ entitlements, isAuthenticated, lang, copy }) => {
  if (!isAuthenticated) return <StoreGate copy={copy} />;
  if (!entitlements) {
    return (
      <div className="mdStore__notice mdCard" data-ui="store-entitlements-loading">
        <p className="mdPage__eyebrow">{copy.entitlementsEyebrow}</p>
        <p>{copy.entitlementsLoading}</p>
      </div>
    );
  }

  const credits = entitlements.credits || {};
  const activeAccess = getActiveAccess(entitlements.access || {});

  return (
    <div className="mdStore__notice mdCard" data-ui="store-entitlements">
      <p className="mdPage__eyebrow">{copy.entitlementsEyebrow}</p>
      <div className="mdStore__entitlementsGrid" data-ui="store-entitlements-grid">
        <div><span>{copy.quickCredits}</span><strong>{credits.quick ?? 0}</strong></div>
        <div><span>{copy.deepCredits}</span><strong>{credits.deep ?? 0}</strong></div>
        <div><span>{copy.followupCredits}</span><strong>{credits.followup ?? 0}</strong></div>
      </div>
      {activeAccess.length ? (
        <div className="mdStore__accessList" data-ui="store-access-list">
          {activeAccess.map((item) => (
            <p key={item.key}><strong>{accessLabel(item.key, copy)}</strong> {copy.activeUntil} {formatDate(item.value, lang)}</p>
          ))}
        </div>
      ) : (
        <p className="mdStore__muted">{copy.noActiveAccess}</p>
      )}
    </div>
  );
};

const OfferVariant = ({ offer, lang, copy, isAuthenticated, buyingSlug, onBuy }) => {
  const localizedOffer = localizeOffer(offer, lang);
  const grantParts = formatGrant(localizedOffer.grant, copy);
  const finalPrice = Number.isFinite(Number(localizedOffer.final_price)) ? Number(localizedOffer.final_price) : Number(localizedOffer.price || 0);
  const originalPrice = Number.isFinite(Number(localizedOffer.original_price)) ? Number(localizedOffer.original_price) : Number(localizedOffer.price || 0);
  const hasPromotion = Boolean(localizedOffer.promotion && finalPrice < originalPrice);
  const promoNote = formatPromotionNote(localizedOffer.promotion, lang, copy);
  const isBuying = buyingSlug === localizedOffer.slug;
  const details = String(localizedOffer.details || "").trim();
  const badge = String(localizedOffer.badge || "").trim();
  const highlight = String(localizedOffer.highlight || "").trim();
  const imageUrl = String(localizedOffer.image_url || "").trim();
  const imageAlt = String(localizedOffer.image_alt || localizedOffer.title || "").trim();

  return (
    <article data-ui="store-offer-variant" data-offer={localizedOffer.slug} data-promo={hasPromotion ? "1" : "0"} data-has-image={imageUrl ? "1" : "0"}>
      {imageUrl && (
        <div data-ui="store-offer-variant-image">
          <img src={imageUrl} alt={imageAlt} loading="lazy" />
        </div>
      )}
      <div data-ui="store-offer-variant-copy">
        {(badge || hasPromotion) && (
          <div className="mdStore__promoBadge" data-ui="store-promo-badge">
            <span>{badge || localizedOffer.promotion.label}</span>
            {hasPromotion && promoNote && <small>{promoNote}</small>}
          </div>
        )}
        <p className="mdPage__eyebrow">{localizedOffer.variant}</p>
        <h3>{localizedOffer.title}</h3>
        {highlight && <p className="mdStore__highlight" data-ui="store-offer-highlight">{highlight}</p>}
        <p>{localizedOffer.description}</p>
        {details && <p className="mdStore__muted" data-ui="store-offer-details">{details}</p>}
        {grantParts.length > 0 && (
          <ul className="mdStore__grant" data-ui="store-offer-grant">
            {grantParts.map((part) => <li key={part}>{part}</li>)}
          </ul>
        )}
      </div>
      <div className="mdStore__offerFoot" data-ui="store-offer-foot">
        <div className="mdStore__price" data-ui="store-price">
          {hasPromotion && <span>{formatPrice(originalPrice, localizedOffer.currency, lang)}</span>}
          <strong>{formatPrice(finalPrice, localizedOffer.currency, lang)}</strong>
        </div>
        {isAuthenticated ? (
          <button
            type="button"
            className="mdBtn mdBtn--primary mdBtn--row"
            onClick={() => onBuy(localizedOffer.slug)}
            disabled={Boolean(buyingSlug)}
            data-ui="store-buy"
          >
            {isBuying ? copy.buying : copy.buy}
          </button>
        ) : (
          <a className="mdBtn mdBtn--primary mdBtn--row" href="#/zaloguj?redirect_to=/sklep" data-ui="store-login-to-buy">
            {copy.loginToBuy}
          </a>
        )}
      </div>
    </article>
  );
};

const ProductCard = ({ group, items, lang, copy, isAuthenticated, buyingSlug, onBuy }) => {
  const highlight = pickGroupHighlight(items, lang);
  return (
    <article className="mdStore__offer mdCard" data-ui="store-product-card" data-group={group}>
      <div data-ui="store-product-card-head">
        <div data-ui="store-product-card-copy">
          <p className="mdPage__eyebrow">{copy.groupLabels[group] || group}</p>
          <h2>{copy.groupLabels[group] || group}</h2>
          <p>{copy.groupSubtitles[group] || ""}</p>
          {highlight && <p className="mdStore__highlight" data-ui="store-group-highlight">{highlight}</p>}
        </div>
      </div>
      <div data-ui="store-offer-variants">
        {items.map((offer) => (
          <OfferVariant
            offer={offer}
            key={offer.slug}
            lang={lang}
            copy={copy}
            isAuthenticated={isAuthenticated}
            buyingSlug={buyingSlug}
            onBuy={onBuy}
          />
        ))}
      </div>
    </article>
  );
};

export const StorePage = () => {
  const { lang } = useLang();
  const copy = getCopy(lang);
  const { isAuthenticated, authReady } = useAuth();
  const [offers, setOffers] = useState([]);
  const [entitlements, setEntitlements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [checkoutStatus, setCheckoutStatus] = useState("");
  const [buyingSlug, setBuyingSlug] = useState("");

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setStatus("");
      const offersResponse = await storeClient.offers();
      if (!mounted) return;
      if (offersResponse?.ok) {
        setOffers(Array.isArray(offersResponse.offers) ? offersResponse.offers : []);
      } else {
        setOffers([]);
        setStatus(offersResponse?.message || copy.loadError);
      }
      setLoading(false);
    };

    load();
    return () => { mounted = false; };
  }, [copy.loadError]);

  useEffect(() => {
    let mounted = true;
    const loadEntitlements = async () => {
      if (!authReady || !isAuthenticated) {
        setEntitlements(null);
        return;
      }
      const response = await storeClient.entitlements();
      if (!mounted) return;
      if (response?.ok) setEntitlements(response.entitlements || null);
      else setEntitlements(null);
    };
    loadEntitlements();
    window.addEventListener("focus", loadEntitlements);
    return () => {
      mounted = false;
      window.removeEventListener("focus", loadEntitlements);
    };
  }, [authReady, isAuthenticated]);

  const handleBuy = async (offerSlug) => {
    if (!offerSlug || buyingSlug) return;
    setCheckoutStatus("");
    setBuyingSlug(offerSlug);
    const response = await storeClient.checkout(offerSlug);
    if (response?.ok && response.checkout_url) {
      window.location.href = response.checkout_url;
      return;
    }
    setCheckoutStatus(response?.message || copy.checkoutError);
    setBuyingSlug("");
  };

  const grouped = useMemo(() => groupOffers(offers), [offers]);
  const groupOrder = ["credits", "access", "full"];
  const visibleGroups = groupOrder.filter((group) => (grouped[group] || []).length > 0);

  return (
    <section className="mdStore mdPage" data-ui="store-page">
      <div className="mdPage__inner mdStore__inner">
        <header className="mdStore__hero mdCard" data-ui="store-hero">
          <p className="mdPage__eyebrow">{copy.heroEyebrow}</p>
          <h1 className="mdPage__title">{copy.heroTitlePrefix} <em>{copy.heroTitleEm}</em> {copy.heroTitleSuffix}</h1>
          <p className="mdPage__subtitle">
            {copy.heroSubtitle}
          </p>
        </header>

        <EntitlementsBox entitlements={entitlements} isAuthenticated={isAuthenticated} lang={lang} copy={copy} />

        {checkoutStatus && <div className="mdStore__notice mdCard" data-ui="store-checkout-status">{checkoutStatus}</div>}
        {loading && <div className="mdStore__notice mdCard" data-ui="store-loading">{copy.loadingOffers}</div>}
        {status && <div className="mdStore__notice mdCard" data-ui="store-error">{status}</div>}

        {!loading && !status && (
          <section className="mdStore__group" data-ui="store-products">
            <div className="mdStore__grid" data-ui="store-products-grid">
              {visibleGroups.map((group) => (
                <ProductCard
                  group={group}
                  items={grouped[group] || []}
                  key={group}
                  lang={lang}
                  copy={copy}
                  isAuthenticated={isAuthenticated}
                  buyingSlug={buyingSlug}
                  onBuy={handleBuy}
                />
              ))}
            </div>
          </section>
        )}
      </div>
    </section>
  );
};
